import { EXPENSE_ROLE, isExpenseAccount } from '../Utils/operatorType';

export const BOOKING_TYPE_LABELS = {
  transfer: { label: 'Transfer', color: 'primary', icon: 'fa-car' },
  experience: { label: 'Experience', color: 'info', icon: 'fa-map-marked-alt' },
  // A cost that is not a trip — car rental, fuel, an admin charge. Every
  // list, badge and timeline in this app renders a booking's type through
  // this map with `|| BOOKING_TYPE_LABELS.transfer`, so the one entry here
  // is what stops an expense being drawn as a car everywhere at once.
  // `fa-receipt` and the muted colour are deliberate: it must not read as
  // something a driver is going to.
  expense: { label: 'Expense', color: 'secondary', icon: 'fa-receipt' },
};

/**
 * Every role an `operators[]` entry can be stored with, in the order the
 * screens offer them. The ONE table: the role picker
 * (MultiOperatorAssignment), the portal's labels (operatorFormat) and the
 * ledger (`formatOrderLineRole`) all read it, so a role cannot be known to
 * one screen and blank on another.
 *
 * The last entry is not crew — see `CREW_ROLES` below. Its receipt icon and
 * dark colour are deliberate: nothing about it may read as a person.
 */
export const OPERATOR_ROLE_LABELS = {
  driver: { label: 'Driver', color: 'primary', icon: 'fa-car' },
  guide: { label: 'Guide', color: 'success', icon: 'fa-user' },
  coordinator: { label: 'Coordinator', color: 'info', icon: 'fa-users' },
  assistant: { label: 'Assistant', color: 'secondary', icon: 'fa-hands-helping' },
  [EXPENSE_ROLE]: { label: 'Cost', color: 'dark', icon: 'fa-receipt' },
};

/**
 * A COST ON A TRIP IS NOT A CREW MEMBER — and since 2026-11-03 it has its
 * own role to say so: `expense`, labelled "Cost".
 *
 * Until then the role enum had no cost member, so `ensureCostOperatorOnBooking`
 * attached an expense account to a real trip as `assistant`, and the office
 * filed cost records the same way. Read back through these labels,
 * "AU-AA-670 Parkolás" rendered as an Assistant with a helping-hands icon —
 * as though a person assisted on the job. Mark decided on 2026-11-02/03 to
 * add the role rather than keep relabelling around it; the API's
 * `EXPENSE_ROLE` docblock (ph-core BookingType.helper) has the reasoning.
 *
 * The distinction is a real one. An expense-ONLY booking is the cost itself
 * (fuel, bérleti díj) and has no trip. On a TRIP, an expense is a cost
 * sitting OUTSIDE the operator's agreed price. For our own drivers it is
 * reimbursed against a cost bucket, which is a different KIND of thing that
 * merely shares the `operators[]` array with the crew. A third party's is
 * filed against the operator itself; until 2026-11-02 it was absorbed, with
 * no line at all.
 *
 * So CREW_ROLES is every role but that one — the API's `CREW_ROLES`, what
 * the portal's assign sheet offers and its schemas accept.
 */
export const CREW_ROLES = Object.keys(OPERATOR_ROLE_LABELS).filter((role) => role !== EXPENSE_ROLE);

export const STOP_TYPE_LABELS = {
  waypoint: { label: 'Waypoint', color: 'info' },
  activity: { label: 'Activity', color: 'primary' },
  break: { label: 'Break', color: 'warning' },
};

/**
 * The badge an `operators[]` entry gets — by TYPE first, then by role.
 *
 * The role alone covers everything filed as a cost since the role existed.
 * The type check stays because stored data still holds expense accounts
 * under crew roles — every bucket until the stored `assistant` entries are
 * converted, and any casual-labour account the office deliberately files as
 * crew — and a cost account is a Cost whatever role it carries. Display
 * only: it never rewrites the stored role.
 *
 * `operator` is the enriched record on the entry (`op.operator`). When it is
 * missing — an unassigned slot — this falls through to the role exactly as
 * before.
 */
export const operatorRoleBadge = (role, operator) => (
  isExpenseAccount(operator)
    ? OPERATOR_ROLE_LABELS[EXPENSE_ROLE]
    : (OPERATOR_ROLE_LABELS[role] || OPERATOR_ROLE_LABELS.driver)
);

export default {
  BOOKING_TYPE_LABELS,
  OPERATOR_ROLE_LABELS,
  CREW_ROLES,
  STOP_TYPE_LABELS,
  operatorRoleBadge,
};
